/**
 * BotAnalyticsCard — compact weekly-glance widget for the admin overview.
 *
 * Reads `/api/admin/chat-bot/summary?days=7` on mount and shows the
 * deflection rate + top unanswered questions. Self-hides when there's
 * been zero activity in the last week (fresh installs / dev environments).
 */
import { useEffect, useState } from "react";
import { Bot, Sparkles } from "lucide-react";
import API from "@/lib/api";

export default function BotAnalyticsCard({ onJump }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/admin/chat-bot/summary?days=7");
        if (!cancelled) setData(data);
      } catch {
        /* silent — card self-hides on any error */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data || (data.total || 0) === 0) return null;

  const rate = Math.round((data.deflection_rate || 0) * 100);
  const rateColor = rate >= 70
    ? "text-emerald-600 dark:text-emerald-400"
    : rate >= 40
      ? "text-amber-600 dark:text-amber-400"
      : "text-rose-600 dark:text-rose-400";

  return (
    <button
      type="button"
      onClick={() => onJump && onJump("chat-bot")}
      className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:border-purple-400 dark:hover:border-purple-500 transition-colors"
      data-testid="bot-analytics-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
          <Bot className="w-5 h-5 text-purple-600 dark:text-purple-300" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Chat Bot — this week</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {data.total} question{data.total === 1 ? "" : "s"} in the last {data.days} days
          </div>
        </div>
        <span className="text-xs font-semibold text-purple-600 dark:text-purple-300">Open →</span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Deflected</div>
          <div className={`text-2xl font-bold ${rateColor}`} data-testid="bot-deflection-rate">{rate}%</div>
        </div>
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Answered</div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{data.replied}</div>
        </div>
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Pending</div>
          <div className="text-2xl font-bold text-rose-600 dark:text-rose-400" data-testid="bot-pending-count">{data.unanswered}</div>
        </div>
      </div>

      {(data.top_unanswered || []).length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-slate-600 dark:text-slate-400">
            <Sparkles className="w-3.5 h-3.5" /> Top unanswered — teach the bot
          </div>
          <ul className="space-y-1.5" data-testid="bot-top-unanswered">
            {data.top_unanswered.slice(0, 5).map((row, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-slate-700 dark:text-slate-300" title={row.question}>
                  {row.question}
                </span>
                <span className="shrink-0 text-xs font-semibold text-slate-500 dark:text-slate-400">×{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </button>
  );
}
